import React from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";

const toDisplay = (value) => {
  if (value === undefined || value === null || value === "") {
    return "—";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
};

const isTrueLike = (value) => value === true || value === "true" || value === 1 || value === "1";
const isFalseLike = (value) => value === false || value === "false" || value === 0 || value === "0";

const Badge = ({ value, tone = "slate" }) => {
  const toneMap = {
    emerald: "border-emerald-500/20 bg-emerald-500/10 text-emerald-300",
    rose: "border-rose-500/20 bg-rose-500/10 text-rose-300",
    amber: "border-amber-500/20 bg-amber-500/10 text-amber-300",
    blue: "border-sky-500/20 bg-sky-500/10 text-sky-300",
    slate: "border-slate-700 bg-slate-800/80 text-slate-200",
  };

  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${toneMap[tone] || toneMap.slate}`}>
      {value}
    </span>
  );
};

const TableSkeleton = () => (
  <div className="space-y-3 p-5">
    {Array.from({ length: 6 }).map((_, index) => (
      <div key={index} className="h-12 animate-pulse rounded-2xl bg-slate-900/90" />
    ))}
  </div>
);

const EmptyState = ({ title, description, action }) => (
  <div className="flex min-h-[240px] flex-col items-center justify-center gap-4 p-8 text-center">
    <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-950/60 p-4 text-slate-300">
      <AlertCircle size={18} />
    </div>
    <div>
      <p className="text-base font-semibold text-white">{title}</p>
      <p className="mt-2 max-w-xl text-sm leading-6 text-slate-400">{description}</p>
    </div>
    {action}
  </div>
);

const columns = [
  { key: "match_id", label: "Match ID" },
  { key: "candidate_name", label: "Candidate Name" },
  { key: "candidate_id", label: "Candidate ID" },
  { key: "candidate_username", label: "Username" },
  { key: "candidate_phone_number", label: "Phone" },
  { key: "candidate_city", label: "City" },
  { key: "candidate_state", label: "State" },
  { key: "candidate_matrimonial_id", label: "Matrimonial ID" },
  { key: "match_score", label: "Match Score" },
  { key: "compatibility_level", label: "Compatibility" },
  { key: "source_service", label: "Source Service" },
  { key: "published", label: "Published" },
  { key: "published_at", label: "Published At" },
  { key: "created_at", label: "Created At" },
  { key: "updated_at", label: "Updated At" },
  { key: "decision", label: "Decision" },
  { key: "is_disliked", label: "Disliked" },
  { key: "is_viewed", label: "Viewed" },
  { key: "is_notified", label: "Notified" },
  { key: "proximity", label: "Proximity" },
];

export default function MatchesHistoryTable({
  rows = [],
  pagination,
  loading,
  error,
  onRetry,
  onPageChange,
  onLimitChange,
  formatDateTime,
}) {
  const limit = Number(pagination?.limit || 20);
  const offset = Number(pagination?.offset || 0);
  const total = Number(pagination?.total || 0);
  const hasMore = Boolean(pagination?.has_more);
  const showingFrom = total > 0 ? offset + 1 : 0;
  const showingTo = total > 0 ? Math.min(offset + rows.length, total) : rows.length;
  const currentPage = Math.floor(offset / Math.max(limit, 1)) + 1;
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / Math.max(limit, 1))) : currentPage;

  return (
    <section className="rounded-3xl border border-slate-800 bg-slate-950/80 shadow-[0_24px_80px_rgba(15,23,42,0.28)]">
      <div className="flex flex-col gap-4 border-b border-slate-800 px-5 py-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">Published Match History</h2>
          <p className="mt-1 text-sm text-slate-400">
            Detailed match publishing records with server-side pagination and backend sorting.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
            {total > 0 ? `Showing ${showingFrom}-${showingTo} of ${total}` : `${rows.length} rows`}
          </div>
          <select
            value={String(limit)}
            onChange={(event) => onLimitChange(Number(event.target.value))}
            className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 outline-none transition focus:border-sky-400"
          >
            {[10, 20, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize} / page
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm font-medium text-slate-200 transition hover:border-sky-500 hover:text-white"
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      </div>

      {loading ? (
        <TableSkeleton />
      ) : error ? (
        <EmptyState
          title="Unable to load history"
          description={error.message || "The history API returned an error for the current filters."}
          action={
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-2 rounded-xl bg-sky-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-sky-400"
            >
              <RefreshCw size={14} />
              Try again
            </button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No published matches found"
          description="Try widening the date range, clearing the candidate filters, or adjusting score and compatibility filters."
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-[2080px] text-left">
              <thead className="bg-slate-900/80">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className="whitespace-nowrap border-b border-slate-800 px-4 py-3 text-xs font-semibold uppercase tracking-[0.24em] text-slate-500"
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const rowKey = row.match_id || row.id || `${row.candidate_id || "candidate"}-${index}`;
                  return (
                    <tr key={rowKey} className="border-b border-slate-900/80 text-sm text-slate-300 transition hover:bg-slate-900/50">
                      <td className="px-4 py-3 font-medium text-white">{toDisplay(row.match_id)}</td>
                      <td className="px-4 py-3 font-medium text-white">{toDisplay(row.candidate_name)}</td>
                      <td className="px-4 py-3">
                        {row.candidate_id ? (
                          <Link to={`/users/user/${row.candidate_id}`} className="font-medium text-sky-300 transition hover:text-sky-200">
                            {row.candidate_id}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3">{toDisplay(row.candidate_username)}</td>
                      <td className="px-4 py-3">{toDisplay(row.candidate_phone_number)}</td>
                      <td className="px-4 py-3">{toDisplay(row.candidate_city)}</td>
                      <td className="px-4 py-3">{toDisplay(row.candidate_state)}</td>
                      <td className="px-4 py-3">{toDisplay(row.candidate_matrimonial_id)}</td>
                      <td className="px-4 py-3">{toDisplay(row.match_score)}</td>
                      <td className="px-4 py-3">
                        <Badge value={toDisplay(row.compatibility_level)} tone="blue" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1.5 items-start">
                          <Badge
                            value={toDisplay(
                              !row.source_service || row.source_service === "unknown"
                                ? "default_service"
                                : row.source_service
                            )}
                            tone="emerald"
                          />
                          {row.created_by && (
                            <div className="flex items-center gap-1 bg-slate-900 px-2 py-0.5 rounded border border-slate-800 text-[10px]">
                              <span className="text-slate-500 font-bold uppercase text-[7px] tracking-tighter">Created By:</span>
                              <span className="font-semibold text-slate-300">{row.created_by}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge value={isTrueLike(row.published) ? "Published" : "No"} tone={isTrueLike(row.published) ? "emerald" : "slate"} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">{formatDateTime(row.published_at)}</td>
                      <td className="whitespace-nowrap px-4 py-3">{formatDateTime(row.created_at)}</td>
                      <td className="whitespace-nowrap px-4 py-3">{formatDateTime(row.updated_at)}</td>
                      <td className="px-4 py-3">
                        <Badge value={toDisplay(row.decision)} tone={isTrueLike(row.decision) ? "emerald" : isFalseLike(row.decision) ? "rose" : "slate"} />
                      </td>
                      <td className="px-4 py-3">
                        <Badge value={toDisplay(row.is_disliked)} tone={isTrueLike(row.is_disliked) ? "rose" : "slate"} />
                      </td>
                      <td className="px-4 py-3">
                        <Badge value={toDisplay(row.is_viewed)} tone={isTrueLike(row.is_viewed) ? "emerald" : "slate"} />
                      </td>
                      <td className="px-4 py-3">
                        <Badge value={toDisplay(row.is_notified)} tone={isTrueLike(row.is_notified) ? "emerald" : "amber"} />
                      </td>
                      <td className="px-4 py-3" title={`${toDisplay(row.proximity)}`}>
                        {toDisplay(row.proximity)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-4 border-t border-slate-800 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-400">
              Page <span className="font-semibold text-white">{currentPage}</span>
              {totalPages ? <> of <span className="font-semibold text-white">{totalPages}</span></> : null}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onPageChange(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 transition hover:border-sky-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft size={14} />
                Previous
              </button>
              <button
                type="button"
                onClick={() => onPageChange(offset + limit)}
                disabled={!hasMore}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 transition hover:border-sky-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
